import React, { useMemo, useEffect, useCallback, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import { compose } from "@reduxjs/toolkit";

import COMMON from "common";
import api from "services/api";
import ERRORS from "common/errors";
import { CALENDAR_FORMAT } from "common/calendar";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import formatStringPattern from "common/format-string-pattern";
import formatPassportString from "common/format-passport-string";
import getBankListing from "services/get-bank-listing";
import getStateListing from "services/get-state-listing";
import getCitiesListing from "services/get-cities-listing";
import getPostcodeListing from "services/get-postcode-listing";
import getNationalityListing from "services/get-nationality-listing";
import getRelationshipListing from "services/get-relationship-listing";
import getMaritalStatusListing from "services/get-marital-status-listing";
import getIdentificationListing from "services/get-identification-listing";
import { AxiosContext } from "contexts/with-interceptor-provider";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppCheckbox from "components/app-checkbox";
import AppButtonInput from "components/app-button-input";
import AppMobileInput from "components/app-mobile-input";
import AppSelectInput from "components/app-select-input";
import AppMaskingInput from "components/app-masking-input";
import AppCalendarInput from "components/app-calendar-input";
import placeholderFormat from "common/placeholder-format";
import classNames from "common/class-names";
import withProfile from "contexts/with-profile";

const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const AppPersonalInfoTab = ( { onHandleGetProfile }) => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const bankRef = useRef();
	const context = useContext(AxiosContext);
	//prettier-ignore
	const dietarySelections = [
        { label: "Halal", value: "HALAL" },
        { label: "Vegan", value: "VEGAN" },
        { label: "Vegetarian", value: "VEGETARIAN" },
        { label: "Other", value: "OTHER" }
    ];
	const initialValues = useMemo(() => ({
		name: "",
		nickname: "",
		idType: "",
		nric: "",
		passport: "",
		dateOfBirth: "",
		gender: "",
		nationality: "",
		maritalStatus: "",
		prefixNo: "",
		mobileNo: "",
		email: "",
		addressLine1: "",
		addressLine2: "",
		state: "",
		postcode: "",
		residentialAddress1: "",
		residentialAddress2: "",
		residentialState: "",
		residentialPostcode: "",
		permanentAddress: false,
		epfNo: "",
		incomeTaxNo: "",
		socsoNo: "",
		bank: "",
		bankAccountNo: "",
		swiftCode: "",
		emergencyContactsFullName: "",
		emergencyContactsRelationship: "",
		emergencyContactsMobileNo: "",
		emergencyContactsPrefixNo: "",
		emergencyContactsFullName2: "",
		emergencyContactsRelationship2: "",
		emergencyContactsMobileNo2: "",
		emergencyContactsPrefixNo2: "",
		dietaryPreference: false,
        dietaryOptions: [],
        foodAllergy: false,
        foodAllergyDetails: "",
        medicalCondition: false,
        medicalConditionDetails: "",
        longTermMedication: false,
        longTermMedicationDetails: "",
        infoConfirmed: false,
        acknowledgePolicy: false,
	}), []);
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			name: yup.string().required(ERRORS.REQUIRED),
			nickname: yup.string().matches(COMMON.REGEX.ALPHABETIC, ERRORS.ALPHABET_ONLY).required(ERRORS.REQUIRED),
			idType: yup.string().required(ERRORS.REQUIRED),
			passport: yup.string().when(["idType"], {
				is: (idType) => idType === COMMON.IDENTIFICATION_TYPE.PASSPORT,
				then: () => yup.string().required(ERRORS.REQUIRED),
			}),
			nric: yup.string().when(["idType"], {
				is: (idType) => idType === COMMON.IDENTIFICATION_TYPE.NRIC,
				then: () => yup.string().required(ERRORS.REQUIRED),
			}),
			dateOfBirth: yup.string().required(ERRORS.REQUIRED),
			gender: yup.string().required(ERRORS.REQUIRED),
			nationality: yup.string().required(ERRORS.REQUIRED),
			maritalStatus: yup.string().required(ERRORS.REQUIRED),
			mobileNo: yup.string().required(ERRORS.REQUIRED).min(8, ERRORS.MOBILE_NO_MIN).max(12, ERRORS.MOBILE_NO_MAX),
			email: yup.string().matches(COMMON.REGEX.EMAIL, ERRORS.EMAIL).required(ERRORS.REQUIRED),
			addressLine1: yup.string().required(ERRORS.REQUIRED),
			state: yup.string().required(ERRORS.REQUIRED),
			postcode: yup.string().required(ERRORS.REQUIRED),
			emergencyContactsFullName: yup.string().matches(COMMON.REGEX.ALPHABETIC, ERRORS.ALPHABET_ONLY).required(ERRORS.REQUIRED),
			emergencyContactsRelationship: yup.string().required(ERRORS.REQUIRED),
			emergencyContactsMobileNo: yup.string().required(ERRORS.REQUIRED).min(8, ERRORS.MOBILE_NO_MIN).max(12, ERRORS.MOBILE_NO_MAX),
			emergencyContactsFullName2: yup.string().matches(COMMON.REGEX.ALPHABETIC, ERRORS.ALPHABET_ONLY),
			emergencyContactsRelationship2: yup.string(),
            emergencyContactsMobileNo2: yup.string().min(8, ERRORS.MOBILE_NO_MIN).max(12, ERRORS.MOBILE_NO_MAX),
			dietaryPreference: yup.boolean().required(ERRORS.REQUIRED),
            dietaryOptions: yup.array().when("dietaryPreference", {
				is: true,
				then: (schema) =>
					schema.test(
						"at-least-one-active",
						"Please select at least one option.",
						(options) => options?.some((opt) => opt.active)
					)
					.test(
						"other-has-description",
						"Please specify your preference.",
						(options) => {
							const other = options?.find((opt) => opt.dietaryOption === "OTHER" && opt.active);
							return !other || !!other.description;
						}
					)
					.required(ERRORS.REQUIRED),
				otherwise: (schema) => schema.notRequired()
			}),
            foodAllergy: yup.boolean().required(ERRORS.REQUIRED),
            foodAllergyDetails: yup.string().when("foodAllergy", {
                is: true,
                then: (schema) => schema.required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            medicalCondition: yup.boolean().required(ERRORS.REQUIRED),
            medicalConditionDetails: yup.string().when("medicalCondition", {
                is: true,
                then: (schema) => schema.required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            longTermMedication: yup.boolean().required(ERRORS.REQUIRED),
            longTermMedicationDetails: yup.string().when("longTermMedication", {
                is: true,
                then: (schema) => schema.required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            infoConfirmed: yup.boolean().oneOf([true], ERRORS.INFO_CONFIRMED).required(ERRORS.INFO_CONFIRMED),
			acknowledgePolicy: yup.boolean().oneOf([true], ERRORS.ACKNOWLEDGE_POLICY).required(ERRORS.ACKNOWLEDGE_POLICY),
			residentialAddress1: yup.string().when(["permanentAddress"], {
				is: (permanentAddress) => !permanentAddress,
				then: () => yup.string().required(ERRORS.REQUIRED),
			}),
			residentialState: yup.string().when(["permanentAddress"], {
				is: (permanentAddress) => !permanentAddress,
				then: () => yup.string().required(ERRORS.REQUIRED),
			}),
			residentialRegion: yup.string().when(["permanentAddress"], {
				is: (permanentAddress) => !permanentAddress,
				then: () => yup.string().required(ERRORS.REQUIRED),
			}),
			residentialPostcode: yup.string().when(["permanentAddress"], {
				is: (permanentAddress) => !permanentAddress,
				then: () => yup.string().required(ERRORS.REQUIRED),
			}),
		}),
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});
	const isPassport = useMemo(() => formik.values.idType === COMMON.IDENTIFICATION_TYPE.PASSPORT, [formik.values.idType]);
	const validIdentificationType = useMemo(() => !!formik.values.idType, [formik.values.idType]);
	const getCitiesLoadOptions = useCallback(() => getCitiesListing({ state: formik.values.state }), [formik.values.state]);
	const getPostcodeLoadOptions = useCallback(() => getPostcodeListing(`${formik.values.state}/${formik.values.region}`), [formik.values.state, formik.values.region]);
	const getResidentialCitiesLoadOptions = useCallback(() => getCitiesListing({ state: formik.values.residentialState }), [formik.values.residentialState]);
	const getResidentialPostcodeLoadOptions = useCallback(() => getPostcodeListing(`${formik.values.residentialState}/${formik.values.residentialRegion}`), [formik.values.residentialState, formik.values.residentialRegion]);
	const memoSetValues = useMemo(() => formik.setValues, [formik]);

	const onHandleSubmit = async (values) => {
		let response = null;

		try {
			let payload = {
				userId: id,
				general: {
					fullName: values.name,
					nickname: values.nickname,
					identificationType: values.idType,
					passport: values.idType === COMMON.IDENTIFICATION_TYPE.PASSPORT ? values.passport : "",
					nric: values.idType === COMMON.IDENTIFICATION_TYPE.NRIC ? values.nric?.split("-")?.join("") : "",
					dateOfBirth: values.dateOfBirth,
					gender: values.gender,
					nationality: values.nationality,
					maritalStatus: values.maritalStatus,
				},
				contactInfo: {
					mobileNoPrefix: values.prefixNo,
					mobileNo: values.mobileNo,
					email: values.email,
					correspondenceAddress1: values.addressLine1,
					correspondenceAddress2: values.addressLine2,
					correspondencePostcode: values.postcode,
					correspondenceState: values.state,
					correspondenceRegion: values.region,
					sameAsCorrespondenceAddress: values.permanentAddress ? "Y" : "N",
				},
				emergencyContact: {
					fullName: values.emergencyContactsFullName,
					relationship: values.emergencyContactsRelationship,
					mobileNoPrefix: values.emergencyContactsPrefixNo,
					mobileNo: values.emergencyContactsMobileNo,
					fullName2: values.emergencyContactsFullName2 || null,
					relationship2: values.emergencyContactsRelationship2 || null,
					mobileNoPrefix2: values.emergencyContactsPrefixNo2 || null,
					mobileNo2: values.emergencyContactsMobileNo2 || null,
				},
				healthDeclaration: {
                    dietaryPreferences: values.dietaryPreference,
                    dietaryOptions: values.dietaryOptions?.filter(option => option.active),
                    foodAllergy: values.foodAllergy,
                    foodAllergyDetails: values.foodAllergyDetails,
                    medicalCondition: values.medicalCondition,
                    medicalConditionDetails: values.medicalConditionDetails,
                    longTermMedication: values.longTermMedication,
                    longTermMedicationDetails: values.longTermMedicationDetails,
                    infoConfirmed: values.infoConfirmed,
                    acknowledgePolicy: values.acknowledgePolicy,
                },
				bankDetail: {
					bankName: values.bank,
					bankNameDesc: bankRef.current.getOptionsData().find((o) => o.value === values.bank).label,
					bankAccount: values.bankAccountNo,
					swiftCode: values.swiftCode,
				},
				contribution: {
					epfNo: values.epfNo,
					incomeTaxNo: values.incomeTaxNo,
					socsoNo: values.socsoNo,
				},
			};

			if (!values.permanentAddress) {
				payload = {
					...payload,
					contactInfo: {
						...payload.contactInfo,
						residentialAddress1: values.residentialAddress1,
						residentialAddress2: values.residentialAddress2,
						residentialPostcode: values.residentialPostcode,
						residentialRegion: values.residentialRegion,
						residentialState: values.residentialState,
					},
				};
			}

			await api.post.employee.updatePersonalInfo(payload);
			await onHandleGetProfile();
			
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			dispatch(promptAlertMessage({ message: "Personal info has been successfully updated" }));
		}
	};

	const onHandleSelectPermanentAddress = (event) => {
		const name = event.target.name;
		const value = event.target.value;
		formik.setFieldValue(name, !value);
	};

	const onHandleCheckbox = useCallback ((event) => {
		const name = event.target.name;
		const value = event.target.value;
		formik.setFieldValue(name, !value);
	}, [formik]);

	const onHandleCancel = () => {
		navigate(-1);
	};

	//prettier-ignore
	const onHandleSelectField = useCallback(async (event) => {
		const name = event.target.name;
		const value = event.target.value;

		switch (name) {
			case "state":
				formik.setFieldValue("region", "");
				formik.setFieldValue("postcode", "");
				break;
			case "region":
				formik.setFieldValue("postcode", "");
				break;
			case "postcode":
				break;
			case "residentialState":
				formik.setFieldValue("residentialRegion", "");
				formik.setFieldValue("residentialPostcode", "");
				break;
			case "residentialRegion":
				formik.setFieldValue("residentialPostcode", "");
				break;
			case "residentialPostcode":
				break;
			default:
				break;
		}

		formik.setFieldValue(name, value);
	}, [formik]);

	useEffect(() => {
		const onHandleGetDetails = async () => {
			let response = null;

			try {
				response = await api.get.employee.personalInfo();
			} catch (error) {
				serveRequestErrors(error);
			}

			if (response) {
				const bank = response?.bank;
				const general = response?.general;
				const contactInfo = response?.contactInfo;
				const contribution = response?.contribution;
				const emergencyContact = response?.emergencyContact;
				const healthDeclaration = response?.healthDeclaration;
				const dietaryOptionsMap = healthDeclaration?.dietaryPreferences?.map((option) => ({
					...option,
					active: true
				}));

				memoSetValues({
					name: general?.name,
					nickname: general?.nickname,
					idType: general?.identificationType,
					nric: general?.nric ? formatStringPattern(general?.nric, COMMON.MASKING.NRIC) : "",
					passport: general?.passport || "",
					dateOfBirth: general?.dateOfBirth,
					gender: general?.gender,
					nationality: general?.nationality || "",
					maritalStatus: general?.maritalStatus,
					mobileNo: contactInfo?.mobileNo,
					prefixNo: contactInfo?.mobileNoPrefix,
					email: contactInfo?.email,
					addressLine1: contactInfo?.correspondenceAddress1 || "",
					addressLine2: contactInfo?.correspondenceAddress2 || "",
					state: contactInfo?.correspondenceState || "",
					region: contactInfo?.correspondenceRegion || "",
					postcode: contactInfo?.correspondencePostcode || "",
					permanentAddress: contactInfo?.sameAsResidentialAddress === "Y",
					residentialAddress1: contactInfo?.residentialAddress1 || "",
					residentialAddress2: contactInfo?.residentialAddress2 || "",
					residentialRegion: contactInfo?.residentialRegion || "",
					residentialState: contactInfo?.residentialState || "",
					residentialPostcode: contactInfo?.residentialPostcode || "",
					bank: bank?.bankName || "",
					bankAccountNo: bank?.bankAccountNo || "",
					swiftCode: bank?.swiftCode || "",
					emergencyContactsFullName: emergencyContact?.fullName || "",
					emergencyContactsRelationship: emergencyContact?.relationship || "",
					emergencyContactsMobileNo: emergencyContact?.mobileNo || "",
					emergencyContactsPrefixNo: emergencyContact?.mobileNoPrefix || "",
					emergencyContactsFullName2: emergencyContact?.fullName2 || "",
					emergencyContactsRelationship2: emergencyContact?.relationship2 || "",
					emergencyContactsMobileNo2: emergencyContact?.mobileNo2 || "",
					emergencyContactsPrefixNo2: emergencyContact?.mobileNoPrefix2 || "",
					dietaryPreference: healthDeclaration?.dietaryPreference,
                    dietaryOptions: dietaryOptionsMap || [],
                    foodAllergy: healthDeclaration?.foodAllergy,
                    foodAllergyDetails: healthDeclaration?.foodAllergyDetails || "",
                    medicalCondition: healthDeclaration?.medicalCondition,
                    medicalConditionDetails: healthDeclaration?.medicalConditionDetails || "",
                    longTermMedication: healthDeclaration?.longTermMedication,
                    longTermMedicationDetails: healthDeclaration?.longTermMedicationDetails || "",
                    infoConfirmed: healthDeclaration?.infoConfirmed,
                    acknowledgePolicy: healthDeclaration?.acknowledgePolicy,
					epfNo: contribution?.epfNo || "",
					incomeTaxNo: contribution?.incomeTaxNo || "",
					socsoNo: contribution?.socsoNo || "",
				});
			}
		};

		onHandleGetDetails();
	}, [memoSetValues]);

	useEffect(() => {
		return () => {
			context.onHandleCancelRequest(COMMON.ENDPOINT_PATH.EMPLOYEE.PERSONAL_INFO);
		};
	}, [context]);

	const rowWidthDietaryClassName = classNames({
        "half-width": formik.values.dietaryPreference === true && formik.values.foodAllergy === true,
        "full-width": !(formik.values.dietaryPreference === true && formik.values.foodAllergy === true),
    });

    const rowWidthMedicalClassName = classNames({
        "half-width": formik.values.medicalCondition === true && formik.values.longTermMedication === true,
        "full-width": !(formik.values.medicalCondition === true && formik.values.longTermMedication === true),
    });

	const toggleDietaryOption = (formik, selectionValue) => {
		const current = formik.values.dietaryOptions;
		const exists = current.find((item) => item.dietaryOption === selectionValue);

		let updated;
		if (!exists) {
			updated = [
			...current,
			{
				dietaryOption: selectionValue,
				description: selectionValue === "OTHER" ? "" : selectionValue,
				active: true
			}
			];
		} else {
			updated = current.map((item) =>
			item.dietaryOption === selectionValue
				? { ...item, active: !item.active }
				: item
			);
		}

		formik.setFieldTouched("dietaryOptions", true);
		formik.setFieldValue("dietaryOptions", updated);
	};

	const renderDietaryPreferencesDescription = (formik) => {
		const index = formik.values.dietaryOptions.findIndex(
			(opt) => opt.dietaryOption === "OTHER"
		);
		const option = formik.values.dietaryOptions[index];

		if (!option?.active) return null;
		return (
			<AppInput
				name={`dietaryOptions[${index}].description`}
				label="Other Preferences"
				disabled={formik.isSubmitting}
				placeholder="Please specify"
				value={option.description || ""}
				error={formik.errors.dietaryOptions}
				touched={formik.touched.dietaryOptions}
				onChange={formik.handleChange}
			/>
		);
	};

	return (
		<div className="app-personal-info-tab">
			<form className="personal-info" onSubmit={formik.handleSubmit}>
				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">General</p>
					</div>

					<div className="personal-info__form">
						{/* prettier-ignore */}
						<AppInput required disabled type="text" name="name" label="Full Name" placeholder="Full Name" value={formik.values.name} error={formik.errors.name} touched={formik.touched.name} onChange={formik.handleChange} />
						{/* prettier-ignore */}
						<AppInput required type="text" name="nickname" label="Nickname" placeholder="Nickname" disabled={formik.isSubmitting} value={formik.values.nickname} error={formik.errors.nickname} touched={formik.touched.nickname} onChange={formik.handleChange} />
						{/* prettier-ignore */}
						<AppSelectInput required disabled type="text" name="idType" label="Identification Type" placeholder="Please Select" loadOptions={getIdentificationListing} value={formik.values.idType} error={formik.errors.idType} touched={formik.touched.idType} onChange={formik.handleChange} />

						{
							/* prettier-ignore */ !isPassport && validIdentificationType && (<AppMaskingInput required disabled type="text" name="nric" label="NRIC No." placeholder="Placeholder" value={formik.values.nric} error={formik.errors.nric} touched={formik.touched.nric} onChange={formik.handleChange} format={COMMON.MASKING.NRIC} />)
						}

						{
							/* prettier-ignore */ isPassport && validIdentificationType && (<AppInput required disabled type="text" name="passport" label="Passport" placeholder="Passport" value={formik.values.passport} error={formik.errors.passport} touched={formik.touched.passport} onFormat={formatPassportString} onChange={formik.handleChange} />)
						}

						{/* prettier-ignore */}
						<AppCalendarInput required disabled name="dateOfBirth" label="Date of Birth" placeholder={placeholderFormat} maxDate={new Date()} displayFormat={calendarDisplayFormat} value={formik.values.dateOfBirth} error={formik.errors.dateOfBirth} touched={formik.touched.dateOfBirth} onChange={formik.setFieldValue} />

						<AppButtonInput required disabled name="gender" label="Gender" value={formik.values.gender} error={formik.errors.gender} touched={formik.touched.gender} onChange={formik.setFieldValue} options={COMMON.DROPDOWNS.GENDER} />
						{/* prettier-ignore */}
						<AppSelectInput required disabled type="text" name="nationality" label="Nationality" placeholder="Please Select" loadOptions={getNationalityListing} value={formik.values.nationality} error={formik.errors.nationality} touched={formik.touched.nationality} onChange={formik.handleChange} />
						{/* prettier-ignore */}
						<AppSelectInput required disabled type="text" name="maritalStatus" label="Marital Status" placeholder="Please Select" loadOptions={getMaritalStatusListing} value={formik.values.maritalStatus} error={formik.errors.maritalStatus} touched={formik.touched.maritalStatus} onChange={formik.handleChange} />
					</div>
				</div>

				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Contact Info</p>
					</div>

					<div className="personal-info__form">
						{/* prettier-ignore */}
						<AppMobileInput required type="number" name="mobileNo" label="Mobile No." placeholder="Mobile No." value={formik.values.mobileNo} prefixNo={formik.values.prefixNo} error={formik.errors.mobileNo} touched={formik.touched.mobileNo} onChange={formik.handleChange} onChangeCode={formik.setFieldValue} disabled={formik.isSubmitting} />

						{/* prettier-ignore */}
						<AppInput required type="text" name="email" label="Personal Email" placeholder="Personal Email" value={formik.values.email} error={formik.errors.email} touched={formik.touched.email} onChange={formik.handleChange} disabled={formik.isSubmitting} />

						{/* prettier-ignore */}
						<AppInput required type="text" name="addressLine1" label="Residential Address" placeholder="Residential Address" value={formik.values.addressLine1} error={formik.errors.addressLine1} touched={formik.touched.addressLine1} onChange={formik.handleChange} disabled={formik.isSubmitting} />

						{/* prettier-ignore */}
						<AppInput type="text" name="addressLine2" label="Address (Line 2)" placeholder="Address (Line 2)" value={formik.values.addressLine2} error={formik.errors.addressLine2} touched={formik.touched.addressLine2} onChange={formik.handleChange} disabled={formik.isSubmitting} />

						{/* prettier-ignore */}
						<AppSelectInput required type="text" name="state" label="State" placeholder="Please Select" loadOptions={getStateListing} value={formik.values.state} error={formik.errors.state} touched={formik.touched.state} onChange={onHandleSelectField} disabled={formik.isSubmitting} />

						{/* prettier-ignore */}
						<AppSelectInput required type="text" name="region" label="Region" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.state} loadOptions={getCitiesLoadOptions} value={formik.values.region} error={formik.errors.region} touched={formik.touched.region} onChange={onHandleSelectField} />

						{/* prettier-ignore */}
						<AppSelectInput required type="text" name="postcode" label="Postcode" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.region} loadOptions={getPostcodeLoadOptions} value={formik.values.postcode} error={formik.errors.postcode} touched={formik.touched.postcode} onChange={onHandleSelectField} />
					</div>
				</div>
				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Permanent Address</p>
					</div>

					{!formik.values.permanentAddress && (
						<div className="personal-info__form">
							{/* prettier-ignore */}
							<AppInput required type="text" name="residentialAddress1" label="Residential Address" placeholder="Residential Address" disabled={formik.isSubmitting} value={formik.values.residentialAddress1} error={formik.errors.residentialAddress1} touched={formik.touched.residentialAddress1} onChange={formik.handleChange} />

							{/* prettier-ignore */}
							<AppInput type="text" name="residentialAddress2" label="Residential Address (Line 2)" placeholder="Address (Line 2)" disabled={formik.isSubmitting} value={formik.values.residentialAddress2} error={formik.errors.residentialAddress2} touched={formik.touched.residentialAddress2} onChange={formik.handleChange} />

							{/* prettier-ignore */}
							<AppSelectInput required type="text" name="residentialState" label="State" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getStateListing} value={formik.values.residentialState} error={formik.errors.residentialState} touched={formik.touched.residentialState} onChange={onHandleSelectField} />

							{/* prettier-ignore */}
							<AppSelectInput required type="text" name="residentialRegion" label="Region" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.residentialState} loadOptions={getResidentialCitiesLoadOptions} value={formik.values.residentialRegion} error={formik.errors.residentialRegion} touched={formik.touched.residentialRegion} onChange={onHandleSelectField} />

							{/* prettier-ignore */}
							<AppSelectInput required type="text" name="residentialPostcode" label="Postcode" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.residentialRegion} loadOptions={getResidentialPostcodeLoadOptions} value={formik.values.residentialPostcode} error={formik.errors.residentialPostcode} touched={formik.touched.residentialPostcode} onChange={onHandleSelectField} />
						</div>
					)}

					<div className="personal-info__form">
						<div className="personal-info__checkbox">
							<AppCheckbox onClick={onHandleSelectPermanentAddress} name="permanentAddress" label="Same as Residential Address" disabled={formik.isSubmitting} value={formik.values.permanentAddress} />
						</div>
					</div>
				</div>

				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Emergency Contact</p>
					</div>

					<div className="personal-info__form">
						{/*prettier-ignore*/}
						<AppInput required type="text" name="emergencyContactsFullName" label="Full Name" placeholder="Full Name" disabled={formik.isSubmitting} value={formik.values.emergencyContactsFullName} error={formik.errors.emergencyContactsFullName} touched={formik.touched.emergencyContactsFullName} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppSelectInput required type="text" name="emergencyContactsRelationship" label="Relationship" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getRelationshipListing} value={formik.values.emergencyContactsRelationship} error={formik.errors.emergencyContactsRelationship} touched={formik.touched.emergencyContactsRelationship} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppMobileInput required type="number" name="emergencyContactsMobileNo" prefixNoName="emergencyContactsPrefixNo" label="Mobile No." disabled={formik.isSubmitting} placeholder="Mobile No." value={formik.values.emergencyContactsMobileNo} prefixNo={formik.values.emergencyContactsPrefixNo} error={formik.errors.emergencyContactsMobileNo} touched={formik.touched.emergencyContactsMobileNo} onChange={formik.handleChange} onChangeCode={formik.setFieldValue} />
					</div>
				</div>

				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Emergency Contact (Optional)</p>
					</div>

					<div className="personal-info__form">
						<AppInput type="text" name="emergencyContactsFullName2" label="Full Name" placeholder="Full Name" disabled={formik.isSubmitting} value={formik.values.emergencyContactsFullName2} error={formik.errors.emergencyContactsFullName2} touched={formik.touched.emergencyContactsFullName2} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppSelectInput type="text" name="emergencyContactsRelationship2" label="Relationship" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getRelationshipListing} value={formik.values.emergencyContactsRelationship2} error={formik.errors.emergencyContactsRelationship2} touched={formik.touched.emergencyContactsRelationship2} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppMobileInput type="number" name="emergencyContactsMobileNo2" prefixNoName="emergencyContactsPrefixNo2" label="Mobile No." disabled={formik.isSubmitting} placeholder="Mobile No." value={formik.values.emergencyContactsMobileNo2} prefixNo={formik.values.emergencyContactsPrefixNo2} error={formik.errors.emergencyContactsMobileNo2} touched={formik.touched.emergencyContactsMobileNo2} onChange={formik.handleChange} onChangeCode={formik.setFieldValue} />
					</div>
				</div>

				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Health Declaration & Consent</p>
					</div>

					<div className="personal-info__form">
						<AppButtonInput
							required
							name="dietaryPreference"
							label="Any dietary preferences or restrictions?"
							disabled={formik.isSubmitting}
							options={[
								{ label: "Yes", value: true },
								{ label: "No", value: false },
							]}
							value={formik.values.dietaryPreference}
							error={formik.errors.dietaryPreference}
							touched={formik.touched.dietaryPreference}
							onChange={formik.setFieldValue}
						/>

						<AppButtonInput
							required
							name="foodAllergy"
							label="Any food allergies or intolerances?"
							disabled={formik.isSubmitting}
							options={[
								{ label: "Yes", value: true },
								{ label: "No", value: false },
							]}
							value={formik.values.foodAllergy}
							error={formik.errors.foodAllergy}
							touched={formik.touched.foodAllergy}
							onChange={formik.setFieldValue}
						/>

						{formik.values.dietaryPreference === true && (
							<div className={rowWidthDietaryClassName}>
								<div className="personal-info__label">
									Please specify your dietary preferences:
								</div>
								{dietarySelections.map((selection) => (
									<div className="personal-info__checkbox">
										<AppCheckbox
											key={selection.value}
											label={selection.label}
											disabled={formik.isSubmitting}
											value={formik.values.dietaryOptions.some(
												(option) => option.dietaryOption === selection.value && option.active
											)}
											onClick={() => toggleDietaryOption(formik, selection.value)}
										/>
									</div>
								))}

								{formik.errors.dietaryOptions !== "Please specify your preference." && (
									<div className="error-message">{formik.errors.dietaryOptions}</div>
								)}

								{renderDietaryPreferencesDescription(formik)}
							</div>
						)}

						{formik.values.foodAllergy === true && formik.values.dietaryPreference !== true && (
							<div className="half-width invi-row"></div>
						)}

						{formik.values.foodAllergy === true && (
							<div className="half-width">
								<AppInput
									name="foodAllergyDetails"
									label="Please specify"
									disabled={formik.isSubmitting}
									placeholder="Please specify"
									value={formik.values.foodAllergyDetails}
									error={formik.errors.foodAllergyDetails}
									touched={formik.touched.foodAllergyDetails}
									onChange={formik.handleChange}
								/>
							</div>
						)}

						<AppButtonInput
							required
							name="medicalCondition"
							label="Any medical condition?"
							disabled={formik.isSubmitting}
							options={[
								{ label: "Yes", value: true },
								{ label: "No", value: false },
							]}
							value={formik.values.medicalCondition}
							error={formik.errors.medicalCondition}
							touched={formik.touched.medicalCondition}
							onChange={formik.setFieldValue}
						/>

						<AppButtonInput
							required
							name="longTermMedication"
							label="Taking any long-term medication?"
							disabled={formik.isSubmitting}
							options={[
								{ label: "Yes", value: true },
								{ label: "No", value: false },
							]}
							value={formik.values.longTermMedication}
							error={formik.errors.longTermMedication}
							touched={formik.touched.longTermMedication}
							onChange={formik.setFieldValue}
						/>


						{formik.values.medicalCondition === true && (
							<div className={rowWidthMedicalClassName}>
								<AppInput
									name="medicalConditionDetails"
									label="Please specify"
									disabled={formik.isSubmitting}
									placeholder="Please specify"
									value={formik.values.medicalConditionDetails}
									error={formik.errors.medicalConditionDetails}
									touched={formik.touched.medicalConditionDetails}
									onChange={formik.handleChange}
								/>
							</div>
						)}

						{formik.values.medicalCondition !== true && formik.values.longTermMedication === true && (
							<div className="half-width invi-row"></div>
						)}

						{formik.values.longTermMedication === true && (
							<div className="half-width">
								<AppInput
									name="longTermMedicationDetails"
									label="Please specify"
									disabled={formik.isSubmitting}
									placeholder="Please specify"
									value={formik.values.longTermMedicationDetails}
									error={formik.errors.longTermMedicationDetails}
									touched={formik.touched.longTermMedicationDetails}
									onChange={formik.handleChange}
								/>
							</div>
						)}

						<div className="full-width">
							<AppCheckbox
								required
								onClick={onHandleCheckbox}
								name="infoConfirmed"
								label={
									<>
										I confirm that the information provided is accurate to the best of my knowledge.
										<span className="required">*</span>										
									</>
								}
								disabled={formik.isSubmitting}
								value={formik.values.infoConfirmed}
								error={formik.errors.infoConfirmed}
								touched={formik.touched.infoConfirmed}
							/>
							<div className="error-message">{formik.errors.infoConfirmed}</div>							
						</div>

						<div className="full-width">
							<div className="acknowledge-policy">
								<AppCheckbox 
								required 
								onClick={onHandleCheckbox} 
								name="acknowledgePolicy" 
								label={
									<>
										I understand that providing false or misleading information may result in workplace actions.
										<span className="required">*</span>	
									</>
								}
								disabled={formik.isSubmitting}
								value={formik.values.acknowledgePolicy}
								error={formik.errors.acknowledgePolicy}
								touched={formik.touched.acknowledgePolicy}
							/>
							<div className="error-message">{formik.errors.acknowledgePolicy}</div>	
							</div>                            						
                        </div> 
					</div>
				</div>

				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Bank Details</p>
					</div>

					<div className="personal-info__form">
						{/* prettier-ignore */}
						<AppSelectInput disabled ref={bankRef} type="text" name="bank" label="Bank Name" placeholder="Please Select" loadOptions={getBankListing} value={formik.values.bank} error={formik.errors.bank} touched={formik.touched.bank} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppInput disabled type="number" name="bankAccountNo" label="Bank Account No." placeholder="Bank Account No." value={formik.values.bankAccountNo} error={formik.errors.bankAccountNo} touched={formik.touched.bankAccountNo} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppInput disabled type="text" name="swiftCode" label="Swift Code / Branch Name" placeholder="Swift Code / Branch Name" value={formik.values.swiftCode} error={formik.errors.swiftCode} touched={formik.touched.swiftCode} onChange={formik.handleChange} />
					</div>
				</div>

				<div className="personal-info__box">
					<div className="personal-info__header">
						<p className="personal-info__title">Contributions</p>
					</div>

					<div className="personal-info__form">
						{/* prettier-ignore */}
						<AppInput disabled type="text" name="epfNo" label="EPF No." placeholder="EPF No." value={formik.values.epfNo} error={formik.errors.epfNo} touched={formik.touched.epfNo} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppInput disabled type="text" name="incomeTaxNo" label="Income Tax No." placeholder="Income Tax No." value={formik.values.incomeTaxNo} error={formik.errors.incomeTaxNo} touched={formik.touched.incomeTaxNo} onChange={formik.handleChange} />

						{/* prettier-ignore */}
						<AppInput disabled type="text" name="socsoNo" label="Socso No." placeholder="Socso No." value={formik.values.socsoNo} error={formik.errors.socsoNo} touched={formik.touched.socsoNo} onChange={formik.handleChange} />
					</div>
				</div>

				<div className="personal-info__button-container">
					<AppButton type="button" label="Cancel" outline onClick={onHandleCancel} />
					<AppButton type="submit" label="Save" />
				</div>
			</form>
		</div>
	);
};

export default compose(withProfile)(AppPersonalInfoTab);
